import { useEffect } from 'react';

const isSameDomainUrl = (href: string): boolean => {
  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return (
      href.startsWith('/') || href.startsWith('./') || href.startsWith('../')
    );
  }
};

const shouldSkipLinkConfirm = (anchor: HTMLAnchorElement): boolean => {
  if (
    anchor.target === '_blank' ||
    anchor.getAttribute('target') === '_blank'
  ) {
    return true;
  }

  if (anchor.closest('[data-skip-cancel-confirm]')) {
    return true;
  }

  return false;
};

export const useConfirmLinkNavigation = (
  shouldConfirm: boolean,
  confirmCancelReordering: () => Promise<boolean>,
  skipBrowserConfirm?: () => void
) => {
  useEffect(() => {
    if (!shouldConfirm) {
      return;
    }

    const handleClick = async (e: MouseEvent) => {
      const target = e.target;
      // For SVG elements
      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest('a');
      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href) {
        return;
      }

      if (shouldSkipLinkConfirm(anchor)) {
        return;
      }

      if (!isSameDomainUrl(href)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const isConfirmed = await confirmCancelReordering();

      if (isConfirmed) {
        skipBrowserConfirm?.();
        window.location.href = href;
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [shouldConfirm, confirmCancelReordering, skipBrowserConfirm]);
};
